import axiosInstance from '~/utils/common-http'
import { useErrorStore } from '~/stores/ErrorStore'
import { handleApiError } from '~/services/http/ApiErrorHandler'
import { getLogoutSignal } from '~/services/auth/useLogoutService'
import type { App } from 'vue'
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { getAuthCookie } from '~/utils/auth-cookie'
import { laravelEcho } from '~/boot/providers/echo'
import { getLocale } from '~/boot/plugins/i18n'

const composedSignalCleanups = new WeakMap<AbortSignal, () => void>()

const composeAbortSignals = (
  requestSignal: AbortSignal,
  logoutSignal: AbortSignal,
): AbortSignal => {
  if (requestSignal === logoutSignal) return requestSignal

  const controller = new AbortController()
  const cleanup = () => {
    requestSignal.removeEventListener('abort', abort)
    logoutSignal.removeEventListener('abort', abort)
    composedSignalCleanups.delete(controller.signal)
  }
  const abort = () => {
    controller.abort()
    cleanup()
  }

  if (requestSignal.aborted || logoutSignal.aborted) {
    controller.abort()
  } else {
    requestSignal.addEventListener('abort', abort, { once: true })
    logoutSignal.addEventListener('abort', abort, { once: true })
    composedSignalCleanups.set(controller.signal, cleanup)
  }

  return controller.signal
}

const releaseComposedSignal = (signal: AbortSignal | undefined): void => {
  if (signal) composedSignalCleanups.get(signal)?.()
}

export default {
  install: (app: App): void => {
    app.config.globalProperties.$http = axiosInstance
    const $http = app.config.globalProperties.$http

    app.provide('$http', $http)

    const handleResponse = (response: AxiosResponse) => {
      releaseComposedSignal(response.config.signal as AbortSignal | undefined)
      useErrorStore().resetErrors()
      return response
    }

    const handleError = (error: AxiosError) => {
      releaseComposedSignal(error.config?.signal as AbortSignal | undefined)
      handleApiError(error)
      return Promise.reject(error)
    }

    $http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = getAuthCookie()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      config.headers['X-Locale'] = getLocale()

      // Add socket ID for Laravel's toOthers() to work correctly
      const socketId = laravelEcho.socketId()
      if (socketId) {
        config.headers['X-Socket-ID'] = socketId
      }

      if (!config.url?.endsWith('/logout')) {
        const logoutSignal = getLogoutSignal()
        config.signal = config.signal
          ? composeAbortSignals(config.signal as AbortSignal, logoutSignal)
          : logoutSignal
      }

      return config
    })

    $http.interceptors.response.use(handleResponse, handleError)
  },
}

export const $http = axiosInstance
